import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import _styles from './text-swatch.module.scss';
import filterRedesignStyles from './v2/text-swatch.module.scss';

/* 
Used to create htmlId for data-cy attribute, so it can be used in tests. It normalizes the string in:
 - converting to lowercase
 - trimming whitespace
 - removing apostrophes
 - replacing non-alphanumeric characters with hyphens
 - collapsing multiple hyphens into one and trimming hyphens from start and end
*/
const normalizeHtmlId = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

interface TextSwatchInterface {
  checked: boolean;
  label: string;
  htmlId: string;
  customClass?: string;
  onChange?: (event: React.SyntheticEvent, value?: string, checked?: boolean) => void;
  useFilterRedesignUi?: boolean;
}

type TextSwatchProps = TextSwatchInterface & Omit<React.HTMLProps<HTMLDivElement>, 'label'>;

const TextSwatch: React.FC<TextSwatchProps & Omit<React.HTMLProps<HTMLDivElement>, 'label'>> = (props) => {
  const { checked, label, htmlId, onChange, customClass, tabIndex = 0, useFilterRedesignUi, ...rest } = props;

  const [checkedState, setCheckedState] = useState(checked);
  const styles = useMemo(() => (useFilterRedesignUi ? filterRedesignStyles : _styles), [useFilterRedesignUi]);
  const normalizedHtmlId = useMemo(() => normalizeHtmlId(htmlId), [htmlId]);

  const handleChange = (event: React.SyntheticEvent, labelValue: string) => {
    onChange?.(event, labelValue, !checkedState);
    setCheckedState(!checkedState);
  };

  useEffect(() => {
    setCheckedState(checked);
  }, [checked]);

  return (
    <div
      key={htmlId}
      role="button"
      className={clsx(styles.textSwatch, customClass, checkedState && styles.checked)}
      onClick={(e) => handleChange(e, label)}
      onKeyPress={(e) => {
        if (e.code === 'Space') {
          handleChange?.(e, label);
        }
      }}
      tabIndex={tabIndex}
      {...rest}
      data-cy={`text-swatch-${normalizedHtmlId}`}
    >
      {label}
    </div>
  );
};

export default TextSwatch;
